import { cn } from "@/lib/utils";
import type { Generic } from "@cohesive/api-entities";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { IntegrationIcon } from "../../IntegrationIcon/IntegrationIcon";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Generic };

/**
 * Card for the platform-agnostic `generic` entity. The slim generic model
 * (id / title / description / url / subtitle / image_url / timestamp +
 * platform_name) is emitted both by selectors fetching a typed toolbox action
 * with `entity_format=generic` and by agents, so this one card renders Drive
 * files, Notion pages, Slack messages, etc. without a per-platform typed card.
 *
 * Two modes mirror `FileFolderCard`: an `embedded` thumbnail tile for canvas
 * media nodes (when an `image_url` is present), and the default list row. The
 * top-level entity `platform` is fixed to "generic"; the real source platform
 * travels in `platform_name`, which drives the logo glyph here.
 */
type GenericCardProps = Generic &
  BaseEntityCardProps & {
    /** Canvas tile mode — fills the React Flow node and shows a thumbnail well. */
    embedded?: boolean;
    /** In embedded mode, hide the title/time footer strip. Defaults to true. */
    showFooter?: boolean;
  };

/**
 * Rewrites a thumbnail URL's trailing Google size token (`=sNNN`) to a larger
 * value so canvas tiles look crisp. Returns the URL unchanged when absent.
 */
function upscaleThumbnail(url: string, size = 640): string {
  return url.replace(/=s\d+(-[^?]*)?(\?.*)?$/, (_, flags, qs) => {
    const suffix = flags ? `=s${size}${flags}` : `=s${size}`;
    return qs ? `${suffix}${qs}` : suffix;
  });
}

/**
 * Candidate image sources for a tile. `image_url` is already a durable URL — the
 * backend's generic mapper substitutes any expiring per-platform thumbnail (e.g.
 * Drive's signed link) with a stable one — so the card just renders it as-is.
 */
function buildImageCandidates(imageURL?: string): string[] {
  return imageURL ? [upscaleThumbnail(imageURL)] : [];
}

/** Relative time (e.g. "2 days ago") or "" when the value isn't a valid date. */
function formatTimestamp(timestamp?: string): string {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return formatDistanceToNow(date, { addSuffix: true });
}

// Logo glyph resolved from the source platform.
export function GenericCardAvatar({
  platform_name,
  className,
  style,
}: Partial<GenericCardProps>) {
  return (
    <BaseEntityCardIcon className={cn("rounded-lg", className)} style={style}>
      <IntegrationIcon
        platform={platform_name}
        name={platform_name}
        className="h-5 w-5 rounded object-cover"
      />
    </BaseEntityCardIcon>
  );
}

export function GenericCard({
  platform_name,
  title,
  description,
  url,
  subtitle,
  image_url,
  timestamp,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  embedded = false,
  showFooter = true,
  className,
  style,
}: GenericCardProps) {
  const displayTitle = title?.trim() || "Untitled";
  // Secondary line: prefer the dedicated subtitle, else the longer description.
  const secondary = subtitle?.trim() || description?.trim();
  const relativeTime = formatTimestamp(timestamp);

  const imageCandidates = useMemo(
    () => buildImageCandidates(image_url),
    [image_url],
  );
  const [imgIndex, setImgIndex] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  useEffect(() => {
    setImgIndex(0);
    setImgLoaded(false);
  }, [imageCandidates]);

  // ── Embedded (canvas tile) mode ───────────────────────────────────────────
  if (embedded) {
    const imgSrc =
      imgIndex < imageCandidates.length ? imageCandidates[imgIndex] : undefined;

    const placeholder = (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-muted/40 to-muted/70">
        <div
          className="flex items-center justify-center rounded-2xl bg-background shadow-sm ring-1 ring-border/60"
          style={{
            width: "clamp(48px, 30cqw, 80px)",
            height: "clamp(48px, 30cqw, 80px)",
          }}
        >
          <IntegrationIcon
            platform={platform_name}
            name={platform_name}
            style={{
              width: "clamp(24px, 16cqw, 44px)",
              height: "clamp(24px, 16cqw, 44px)",
            }}
            className="object-contain"
          />
        </div>
      </div>
    );

    return (
      <BaseEntityCard
        layout="stack"
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        processes={processes}
        className={cn(
          "group relative overflow-hidden transition-all duration-200",
          "w-full h-full min-h-0 rounded-xl border-0 shadow-none hover:shadow-2xl flex flex-col bg-card [&>div]:flex [&>div]:flex-1 [&>div]:min-h-0 [&>div]:flex-col",
          className,
        )}
        style={{ containerType: "inline-size", ...style }}
      >
        <div className="relative flex-1 min-h-0 w-full overflow-hidden bg-card">
          {!(imgSrc && imgLoaded) && placeholder}
          {imgSrc && (
            <img
              src={imgSrc}
              alt={displayTitle}
              loading="eager"
              decoding="async"
              referrerPolicy="no-referrer"
              className={cn(
                "absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-200",
                imgLoaded ? "opacity-100" : "opacity-0",
              )}
              onLoad={() => setImgLoaded(true)}
              onError={() => {
                setImgLoaded(false);
                setImgIndex((i) => i + 1);
              }}
            />
          )}
        </div>
        {showFooter && (
          <div className="flex items-center gap-2 shrink-0 px-2.5 py-2 border-t bg-card/80">
            <IntegrationIcon
              platform={platform_name}
              name={platform_name}
              className="h-3.5 w-3.5 shrink-0 object-contain"
            />
            <p className="flex-1 min-w-0 text-[12px] font-semibold text-foreground leading-tight truncate">
              {displayTitle}
            </p>
            {relativeTime && (
              <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                {relativeTime}
              </span>
            )}
          </div>
        )}
      </BaseEntityCard>
    );
  }

  // ── Row (list) mode ───────────────────────────────────────────────────────
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      align={secondary ? "start" : "center"}
      className={className}
      style={style}
    >
      <GenericCardAvatar platform_name={platform_name} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle>{displayTitle}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {relativeTime && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {relativeTime}
              </span>
            )}
            {url && <BaseEntityCardExternalLinkAction href={url} />}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        {secondary && (
          <BaseEntityCardBody>
            <BaseEntityCardDescription className="line-clamp-3">
              {secondary}
            </BaseEntityCardDescription>
          </BaseEntityCardBody>
        )}
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
